import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { listPublishedOutlooks } from "@/lib/weekly-outlook.functions";
import { RegimeBadge } from "@/components/weekly-outlook/WeeklyOutlookView";

const CANONICAL = "https://xauproacademy.com/weekly-outlook";
const TITLE = "Gold Weekly Outlook: XAUUSD Review | XAU Pro Academy";
const DESC = "Weekly XAUUSD research: a full review of every closed desk decision in R with candle replays, followed by the liquidity map and macro catalysts for the week ahead.";


export const Route = createFileRoute("/weekly-outlook/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Gold Weekly Outlook Archive" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Gold Weekly Outlook Archive" },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "CollectionPage", name: "Gold Weekly Outlook Archive", description: DESC, url: CANONICAL }) }],
  }),
  loader: () => listPublishedOutlooks(),
  component: Hub,
});

function Hub() {
  const data = Route.useLoaderData();
  const [latest, ...rest] = data;
  return (
    <Layout>
      <div className="container mx-auto max-w-5xl px-4 py-12">
        <p className="font-mono text-[10px] uppercase tracking-wider text-gold">Research desk</p>
        <h1 className="mt-2 font-display text-3xl font-bold md:text-4xl">Gold Weekly Outlook</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">{DESC}</p>

        {!latest ? (
          <p className="mt-10 rounded-xl border border-border/60 bg-card p-6 text-sm text-muted-foreground">Awaiting first published report.</p>
        ) : (
          <>
            <Link to="/weekly-outlook/$slug" params={{ slug: latest.slug }} className="mt-10 block rounded-2xl border border-gold/40 bg-gradient-to-br from-card to-background p-8 transition-colors hover:border-gold">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-wider text-gold">Latest</span>
                <RegimeBadge regime={latest.regime} />
              </div>
              <h2 className="mt-3 font-display text-2xl font-semibold">{latest.title}</h2>
              <p className="mt-2 text-muted-foreground">{latest.summary}</p>
              <p className="mt-4 font-mono text-sm">
                {latest.content.totals.trades} trades · {latest.content.totals.netR >= 0 ? "+" : ""}{latest.content.totals.netR}R
              </p>
            </Link>
            {rest.length > 0 && (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((o) => (
                  <Link key={o.slug} to="/weekly-outlook/$slug" params={{ slug: o.slug }} className="rounded-xl border border-border/60 bg-card p-5 transition-colors hover:border-gold/50">
                    <RegimeBadge regime={o.regime} />
                    <h3 className="mt-3 font-display font-semibold">{o.title}</h3>
                    <p className="mt-2 font-mono text-xs text-muted-foreground">
                      {o.content.totals.trades} trades · {o.content.totals.netR >= 0 ? "+" : ""}{o.content.totals.netR}R
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
